import React from 'react';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { HorizontalBarChart } from '@/components/ui';
import { useAdminJobsStats } from '@/hooks/queries';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

export function JobsDashboard() {
  const [windowVal, setWindowVal] = React.useState<'24h'|'7d'|'30d'>('24h');
  const { data, isLoading: loading } = useAdminJobsStats(windowVal);

  usePageHeader({ title: t('Background Jobs'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }] });

  return (
    <PageContainer>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm text-muted-foreground">Window:</label>
        <select value={windowVal} onChange={(e) => setWindowVal(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
          <option value="24h">24h</option>
          <option value="7d">7d</option>
          <option value="30d">30d</option>
        </select>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : !data ? (
        <div>No data</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent>
                <div className="text-sm text-muted-foreground">Average runtime</div>
                <div className="text-2xl font-semibold">{Math.round((data.runtime?.avgMs || 0))} ms</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-sm text-muted-foreground">Oldest queued at</div>
                <div className="text-2xl font-semibold">{data.oldestQueuedAt ? `${formatDate(data.oldestQueuedAt)} ${formatTime(data.oldestQueuedAt)}` : '—'}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="text-sm text-muted-foreground">Statuses</div>
                <div className="text-sm">{(data.byStatus || []).map((s: any) => `${s.status}: ${s.count}`).join(' · ') || '—'}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Average runtime by job type</CardTitle>
              <CardDescription>Sorted by highest average runtime</CardDescription>
            </CardHeader>
            <CardContent>
              {Array.isArray(data.runtimeByType) && data.runtimeByType.length > 0 ? (
                <HorizontalBarChart
                  unit="ms"
                  data={data.runtimeByType.map((r: any) => ({ label: `${r.type} (${r.count})`, value: r.avgMs }))}
                />
              ) : (
                <div className="text-sm text-muted-foreground">No completed jobs with runtime yet.</div>
              )}
            </CardContent>
          </Card>

          <Card variant="bordered">
            <CardHeader>
              <CardTitle className="text-base">Background jobs overview</CardTitle>
              <CardDescription>Registered types, schedules, and current activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-foreground mb-2">Registered job types</div>
                  <div className="space-y-3">
                    {(data.jobTypes || []).map((jt: any) => (
                      <div key={jt.type} className="border rounded p-3">
                        <div className="text-sm font-medium text-foreground">{jt.name} <span className="text-muted-foreground">({jt.type})</span></div>
                        <div className="text-sm text-foreground mt-1">{jt.description}</div>
                        <div className="text-xs text-muted-foreground mt-1">Trigger: {jt.trigger}{jt.schedule ? ` · Schedule: ${jt.schedule}` : ''}</div>
                      </div>
                    ))}
                    {(!data.jobTypes || data.jobTypes.length === 0) && (
                      <div className="text-sm text-muted-foreground">No registered job types</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground mb-2">Current activity</div>
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">As of {data.asOf ? formatTime(data.asOf) : ''}</div>
                    <ul className="text-sm text-foreground list-disc pl-5">
                      {(data.runningNow || []).map((r: any) => (
                        <li key={r.type}>{r.type}: {r.count}</li>
                      ))}
                      {(!data.runningNow || data.runningNow.length === 0) && (
                        <li className="list-none text-muted-foreground">No jobs processing</li>
                      )}
                    </ul>
                  </div>
                  <div className="mt-6">
                    <div className="text-sm font-medium text-foreground mb-2">Schedules</div>
                    <ul className="text-sm text-foreground">
                      <li><span className="text-muted-foreground">Worker:</span> {data.schedules?.worker || '—'}</li>
                      <li><span className="text-muted-foreground">Drafts TTL:</span> {data.schedules?.draftsTtl || '—'}</li>
                      <li><span className="text-muted-foreground">Embeddings:</span> {data.schedules?.embeddings || '—'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  );
}

function maxAvgMs(rows: Array<{ avgMs: number }>) {
  return rows.reduce((m, r) => (r.avgMs > m ? r.avgMs : m), 0) || 1;
}

function BarRow({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3">
      <div className="w-48 shrink-0 text-xs text-foreground truncate" title={label}>{label}</div>
      <div className="flex-1 h-4 bg-muted rounded">
        <div
          className="h-4 bg-primary-500 rounded"
          style={{ width: `${pct}%` }}
          aria-label={`${label}: ${Math.round(value)} ms`}
        />
      </div>
      <div className="w-16 text-right text-xs text-muted-foreground">{Math.round(value)} ms</div>
    </div>
  );
}


